import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useMaintenanceContext, type GracePhase } from "@/contexts/MaintenanceContext";
import type { SaveStatus } from "./useMaintenanceAutoSave";

export type AutoSaveStatus = SaveStatus;

export interface GracePeriodBannerState {
  isActive: boolean;
  phase: GracePhase;
  gracePeriodEndsAt: Date | null;
  message: string | null;
  estimatedEnd: string | null;
  autoSaveStatus: AutoSaveStatus;
  modalOpen: boolean;
  onModalOpenChange: (open: boolean) => void;
  onPhaseChange: (phase: GracePhase) => void;
  onComplete: () => void;
  /** Achado MÉDIO (E2E, 28/09/2026, item 7): true só enquanto a carência
   * está DE VERDADE em curso — fase válida (nunca "expired", que o /status
   * manda quando ela já passou) e gracePeriodEndsAt no futuro. Sem isso, o
   * banner/modal de carência aparecia DEPOIS de acabar, inclusive sobre a
   * tela de login e sobre /maintenance, com o fundo escuro interceptando
   * cliques. MaintenanceOverlay usa este campo — não `isActive && phase &&
   * gracePeriodEndsAt` (phase truthy não significa fase válida). */
  carenciaEmCurso: boolean;
}

export function useGracePeriodBanner(): GracePeriodBannerState {
  const ctx = useMaintenanceContext();
  const [modalOpen, setModalOpen] = useState(false);
  const autoSaveTriggeredRef = useRef(false);
  const modalShownRef = useRef<string | null>(null);

  const gracePeriodEndsAt = useMemo(
    () => (ctx.gracePeriodEndsAt ? new Date(ctx.gracePeriodEndsAt) : null),
    [ctx.gracePeriodEndsAt]
  );

  // "expired" (string real que o backend manda) não é membro de GracePhase,
  // mas um `as GracePhase` em algum ponto upstream pode deixá-la passar
  // assim mesmo — é truthy em JS. Checagem explícita, não confia só em
  // `ctx.phase` ser truthy.
  const faseEhValida =
    ctx.phase === "warn" ||
    ctx.phase === "save" ||
    ctx.phase === "readonly" ||
    ctx.phase === "logout";
  const carenciaEmCurso =
    ctx.isActive &&
    faseEhValida &&
    gracePeriodEndsAt !== null &&
    gracePeriodEndsAt.getTime() > Date.now();

  // Open modal once per maintenance session (tracked by gracePeriodEndsAt)
  const gracePeriodKey = ctx.gracePeriodEndsAt;
  useEffect(() => {
    if (carenciaEmCurso && gracePeriodKey && modalShownRef.current !== gracePeriodKey) {
      modalShownRef.current = gracePeriodKey;
      setModalOpen(true);
    }
  }, [carenciaEmCurso, gracePeriodKey]);

  // Story 3.5: Trigger auto-save event when entering "save" phase
  // Status is tracked by useMaintenanceAutoSave via context — no local state needed
  useEffect(() => {
    if (ctx.phase === "save" && !autoSaveTriggeredRef.current) {
      autoSaveTriggeredRef.current = true;
      window.dispatchEvent(new CustomEvent("maintenance:autosave"));
    }
    if (ctx.phase !== "save") {
      autoSaveTriggeredRef.current = false;
    }
  }, [ctx.phase]);

  const onModalOpenChange = useCallback((open: boolean) => {
    setModalOpen(open);
  }, []);

  const onPhaseChange = useCallback((_phase: GracePhase) => {
    // Phase tracked by context — banner visual changes are CSS-driven
  }, []);

  const onComplete = useCallback(() => {
    // Grace period ended — redirect to maintenance page or logout
    window.location.href = "/maintenance";
  }, []);

  return {
    isActive: ctx.isActive,
    phase: ctx.phase,
    gracePeriodEndsAt,
    message: ctx.message,
    estimatedEnd: ctx.estimatedEnd,
    autoSaveStatus: ctx.saveStatus,
    modalOpen,
    onModalOpenChange,
    onPhaseChange,
    onComplete,
    carenciaEmCurso,
  };
}
